import React, { useState, useRef, useEffect } from "react";
import Markdown from "markdown-to-jsx";
import SkillsCard from "./SkillsCard";
import WorkExperienceCard from "./WorkExperienceCard";
import ProjectsCard from "./ProjectCard";
import ContactCard from "./ContactCard";
import { handleQuickButton, quickButtons } from "../utils/buttonHandlers";
import "../App.css";

function ChatScreen({ onBack, isLoading, setIsLoading }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showQuickButtons, setShowQuickButtons] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [currentVideoSrc, setCurrentVideoSrc] = useState("/avatar_active.mp4");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);

  const handleQuickButtonClick = async (label) => {
    await handleQuickButton(label, setMessages, setIsLoading);
  };

  const LoadingDots = () => (
    <span
      style={{
        animation: "blink 1.2s ease-in-out infinite",
        fontSize: "20px",
        fontWeight: "bold",
      }}
    >
      ...
    </span>
  );

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      setScrollY(scrollTop);

      // Show indicator if there's content above (not at the very top)
      // This helps users know they can scroll up to see more
      const hasScrollableContent = scrollHeight > clientHeight;
      const isNotAtTop = scrollTop > 50; // More than 50px from top

      setCanScrollUp(hasScrollableContent && isNotAtTop);
      setShowScrollIndicator(hasScrollableContent);
    }
  };

  const avatarStyle = {
    width:
      currentVideoSrc === "/avatar_thinks.mp4"
        ? 180
        : currentVideoSrc === "/avatar-listening-new-unscreen.gif"
        ? 120
        : 90,
    height:
      currentVideoSrc === "/avatar_thinks.mp4"
        ? 120
        : currentVideoSrc === "/avatar-listening-new-unscreen.gif"
        ? 120
        : "auto",
    display: "block",
    margin: "0 auto",
    transition: "width 0.3s ease, height 0.3s ease",
    objectFit: "cover",
    // filter: isLoading
    //   ? "drop-shadow(0 0 8px rgba(92, 124, 250, 0.4))"
    //   : undefined,
  };

  // Determine which video source to use
  const getVideoSource = () => {
    if (isTyping) return "/avatar-listening-new-unscreen.gif";
    if (isLoading) return "/avatar_thinks.mp4";
    return "/avatar_active.mp4";
  };

  // Handle smooth video transitions
  useEffect(() => {
    const newVideoSrc = getVideoSource();
    if (newVideoSrc !== currentVideoSrc) {
      setCurrentVideoSrc(newVideoSrc);
    }
  }, [isTyping, isLoading, currentVideoSrc]);

  useEffect(() => {
    if (chatEndRef.current)
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    // Check scroll position after messages update
    setTimeout(() => {
      handleScroll();
    }, 100);
  }, [messages]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      // Initial check
      handleScroll();
      // Listen for scroll events
      chatContainer.addEventListener("scroll", handleScroll);
      // Also check when content size changes
      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(chatContainer);

      return () => {
        chatContainer.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, []);

  const handleSend = async (overrideText) => {
    const messageToSend = (overrideText ?? input).trim();
    if (!messageToSend || isLoading) return;

    setIsLoading(true);
    setMessages((msgs) => [...msgs, { type: "user", text: messageToSend }]);
    if (!overrideText) setInput("");
    try {
      const res = await fetch(
        "https://ai-portfolio-backend-w6r0.onrender.com/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: messageToSend }),
        }
      );
      const data = await res.json();
      setMessages((msgs) => [...msgs, { type: "bot", text: data.response }]);
    } catch {
      setMessages((msgs) => [
        ...msgs,
        { type: "bot", text: "Error: Server unreachable." },
      ]);
    }
    setIsLoading(false);
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        alignItems: "center",
        overflow: "hidden",
        maxWidth: "100vw",
        padding: "0 10px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ position: "absolute", right: 28, top: 5, zIndex: 20 }}>
        <button
          onClick={onBack}
          className="chat-back-button"
          style={{
            background: "#f7f9fb",
            border: "1px solidrgb(15, 17, 21)",
            borderRadius: "100px",
            padding: "8px 18px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            color: "#242424",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          ← Back
        </button>
      </div>

      {/* Top greeting/message */}
      <div
        style={{
          textAlign: "center",
          marginTop: 20,
          marginBottom: 16,
          position: "relative",
        }}
      >
        {currentVideoSrc.endsWith(".gif") ? (
          <img
            src={currentVideoSrc}
            alt="Avatar"
            style={{
              ...avatarStyle,
              borderRadius: "10px",
            }}
            className="chat-avatar"
          />
        ) : (
          <video
            src={currentVideoSrc}
            autoPlay
            loop
            muted
            preload="auto"
            style={{
              ...avatarStyle,
              borderRadius: "10px",
            }}
            className="chat-avatar"
          />
        )}
        <div style={{ fontWeight: "bold", fontSize: "16px", marginTop: "5px" }}>
          Hey there! Vibhor here—ask me anything about me.{" "}
          <span role="img" aria-label="sunglasses">
            😎
          </span>
        </div>
      </div>

      {/* Chat history */}
      <div
        ref={chatContainerRef}
        className="chat-container-scrollable"
        style={{
          flex: "1 1 auto",
          overflowY: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          margin: "0",
          padding: "0px",
          position: "relative",
        }}
      >
        {/* Scroll up indicator */}
        {canScrollUp && (
          <div
            style={{
              position: "sticky",
              top: "10px",
              zIndex: 100,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              pointerEvents: "none",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                background: "rgba(92, 124, 250, 0.9)",
                color: "white",
                padding: "8px 16px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                animation: "fadeInDown 0.3s ease-out",
              }}
            >
              <span>↑</span>
              <span>Scroll up for more</span>
            </div>
          </div>
        )}
        <div
          className="chat-messages-wrapper"
          style={{ width: "100%", maxWidth: "670px" }}
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent:
                  msg.type === "user"
                    ? "flex-end"
                    : msg.type === "bot"
                    ? "flex-start"
                    : "center",
                marginBottom: 12,
              }}
            >
              {msg.type === "contact_card" ? (
                <ContactCard />
              ) : msg.type === "skills_card" ? (
                <SkillsCard />
              ) : msg.type === "work_card" ? (
                <WorkExperienceCard />
              ) : msg.type === "projects_card" ? (
                <ProjectsCard />
              ) : (
                <span
                  style={{
                    display: "inline-block",
                    background: msg.type === "user" ? "#e6ebfb" : "#f6f7fa",
                    color: "#222",
                    borderRadius: "14px",
                    padding: "9px 16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    maxWidth: "87%",
                    fontSize: "14px",
                    wordBreak: "break-word",
                    textAlign: "left",
                  }}
                >
                  {msg.type === "bot" ? (
                    <Markdown
                      options={{
                        forceBlock: true,
                        overrides: {
                          a: {
                            component: (props) => (
                              <a {...props} target="_blank" rel="noreferrer" />
                            ),
                          },
                        },
                      }}
                    >
                      {msg.text}
                    </Markdown>
                  ) : (
                    msg.text
                  )}
                </span>
              )}
            </div>
          ))}
          {/* Show loading animation when waiting for bot response */}
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  background: "#f6f7fa",
                  color: "#222",
                  borderRadius: "14px",
                  padding: "9px 16px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  maxWidth: "87%",
                  fontSize: "20px",
                  fontWeight: "bold",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#222",
                    animation: "dotPulse 1.4s ease-in-out infinite",
                    animationDelay: "0s",
                  }}
                />
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#222",
                    animation: "dotPulse 1.4s ease-in-out infinite",
                    animationDelay: "0.2s",
                  }}
                />
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#222",
                    animation: "dotPulse 1.4s ease-in-out infinite",
                    animationDelay: "0.4s",
                  }}
                />
              </span>
            </div>
          )}
          <span ref={chatEndRef} />
        </div>
      </div>

      {/* Quick questions Toggle Link */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: showQuickButtons ? "0" : "10px",
          marginTop: "18px",
        }}
      >
        <button
          onClick={() => setShowQuickButtons((val) => !val)}
          style={{
            background: "none",
            border: "none",
            color: "#222345",
            fontWeight: 500,
            fontSize: 16,
            cursor: "pointer",
            outline: "none",
            padding: 0,
            marginBottom: showQuickButtons ? 2 : 8,
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              display: "inline-block",
              transform: showQuickButtons ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.18s",
            }}
          >
            ▼
          </span>
          <span style={{ marginLeft: 8 }}>
            {showQuickButtons ? "Hide quick questions" : "Show quick questions"}
          </span>
        </button>
      </div>

      {/* Quick question buttons section */}
      {showQuickButtons && (
        <div
          className="quick-questions-container"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "18px",
            marginBottom: "15px",
            marginTop: "10px",
          }}
        >
          {quickButtons.map(({ label, icon }) => (
            <button
              className="quick-question-button"
              key={label}
              onClick={() => handleQuickButtonClick(label)}
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "999px",
                padding: "8px 14px",
                fontSize: "13px",
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              <span style={{ fontSize: 17, marginRight: 8 }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      <div
        className="chat-input-container"
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          position: "sticky",
          bottom: 8,
          zIndex: 10,
          background: "transparent",
        }}
      >
        <div
          style={{
            width: "670px",
            background: "#f7f9fb",
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            padding: "4px 8px",
          }}
        >
          <input
            className="chat-input"
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: "16px",
              padding: "16px",
              width: "100%",
              borderRadius: "20px",
            }}
            placeholder="Ask me anything"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setIsTyping(true); // start "typing" state on text change
            }}
            onBlur={() => setIsTyping(false)} // stop typing when input loses focus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSend();
                setIsTyping(false); // stop typing when message sent
              }
            }}
          />
          <button
            className="chat-send-button"
            disabled={isLoading}
            style={{
              background: "linear-gradient(90deg,#5C7CFA,#9775FA)",
              border: "none",
              borderRadius: "50%",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "8px",
              cursor: "pointer",
            }}
            onClick={() => {
              console.log("isLoading:", isLoading);
              handleSend();
            }}
          >
            <svg width="23" height="23" viewBox="0 0 24 24" fill="none">
              <path
                d="M8 12h8m0 0l-3-3m3 3l-3 3"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Footer username */}
      <div
        className="footer-username"
        style={{
          position: "fixed",
          right: "24px",
          bottom: "18px",
          color: "#1f1f1f9a",
          fontSize: "14px",
          opacity: 0.6,
          letterSpacing: "0.5px",
        }}
      >
        @matvibhor24
      </div>
    </div>
  );
}

export default ChatScreen;
